import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

/**
 * Types de toast disponibles:
 * - success: Action réussie (vert)
 * - error: Erreur (rouge)
 * - warning: Avertissement (orange)
 * - info: Information (bleu)
 */

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, options = {}) => {
    const id = ++toastIdCounter;
    const toast = {
      id,
      message,
      type: options.type || 'info',
      duration: options.duration || 4000,
      icon: options.icon || null,
      title: options.title || null,
      action: options.action || null, // { label: string, onClick: () => void }
    };

    setToasts((prev) => [toast, ...prev]);

    // Auto-remove après la durée
    if (toast.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, toast.duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Raccourcis pour les différents types
  const success = useCallback(
    (message, options = {}) => addToast(message, { ...options, type: 'success' }),
    [addToast]
  );

  const error = useCallback(
    (message, options = {}) => addToast(message, { ...options, type: 'error' }),
    [addToast]
  );

  const warning = useCallback(
    (message, options = {}) => addToast(message, { ...options, type: 'warning' }),
    [addToast]
  );

  const info = useCallback(
    (message, options = {}) => addToast(message, { ...options, type: 'info' }),
    [addToast]
  );

  // Écouter les événements globaux (rate limit, erreurs API)
  useEffect(() => {
    const handleRateLimit = (event) => {
      const { message } = event.detail;
      addToast(message, { 
        type: 'warning', 
        title: 'Limite atteinte',
        duration: 6000 
      });
    };

    const handleApiError = (event) => {
      const { message } = event.detail;
      addToast(message, { type: 'error', duration: 5000 });
    };

    window.addEventListener('api-rate-limit', handleRateLimit);
    window.addEventListener('api-error', handleApiError);

    return () => {
      window.removeEventListener('api-rate-limit', handleRateLimit);
      window.removeEventListener('api-error', handleApiError);
    };
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
